import LegalLayout, { Section, Note } from "./LegalLayout";
import { BRAND } from "@/data/content";
import SEO from "@/components/SEO";

const UPDATED = "December 2025";

export default function Terms() {
  return (
    <LegalLayout eyebrow="Legal · 02" title="Terms of Service" updated={UPDATED}>
      <SEO
        title="Terms of Service"
        description="The terms governing your use of NEXTGEN PERFORMANCE coaching, nutrition, and peptide-inquiry services."
        url="https://nextgenlifestyle365.com/terms"
      />
      <p data-testid="terms-intro" className="text-white/75 font-light leading-relaxed">
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of this
        website and any coaching, nutrition, or peptide-inquiry service offered
        by {BRAND.name} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;Coach J&rdquo;). By using
        the site or purchasing a program, you agree to these Terms in full.
      </p>

      <Section title="1. Eligibility">
        <p>You must be at least 18 years old and legally able to enter into a binding contract to use our services. You confirm that all information you provide is accurate and current.</p>
      </Section>

      <Section title="2. Services Offered">
        <p><strong className="text-white">Coaching &amp; Programs.</strong> We
        offer online and (where available) in-person coaching packages
        including the Foundation Package ($299/mo), Transformation Package
        ($449/mo), and Peak Performance Package ($699/mo). Specific
        deliverables for each package are described on the site and confirmed
        at onboarding.</p>
        <p><strong className="text-white">Peptide Inquiries.</strong> Any
        peptide-related information on this site is for education and
        research-use inquiry only. See our{" "}
        <a href="/medical-disclaimer" className="text-ng-blue hover:underline">Medical Disclaimer</a>{" "}
        for critical limitations.</p>
      </Section>

      <Section title="3. No Medical Advice · No Guaranteed Results">
        <p>Coach J is an ISSA-certified personal trainer, nutrition coach, and
        bodybuilding specialist &mdash; not a licensed medical professional.
        Nothing on this site or in any coaching relationship constitutes
        medical advice, diagnosis, or treatment.</p>
        <p><strong className="text-white">Consult your physician</strong> before
        starting any exercise program, changing your diet, using supplements,
        or considering any peptide-related decision. Results depend on
        individual effort, genetics, adherence, and starting point;
        testimonials shown are individual experiences and are not typical or
        guaranteed.</p>
      </Section>

      <Section title="4. Payment, Billing & Refunds">
        <p><strong className="text-white">Payments</strong> are processed by
        Stripe. Recurring monthly programs (where offered) will bill your card
        automatically on the same day each month until you cancel.</p>
        <p><strong className="text-white">Cancellation.</strong> You may cancel
        a monthly program at any time by emailing <a href={`mailto:${BRAND.email}`} className="text-ng-blue hover:underline">{BRAND.email}</a>{" "}
        at least 3 business days before your next billing date. Access to
        coaching services continues through the end of the paid period.</p>
        <p><strong className="text-white">Refunds.</strong> Because coaching is
        a highly personalized service, all sales are final and no refunds are
        issued once onboarding materials, custom programs, or nutrition plans
        have been delivered. If you experience a technical or delivery issue
        within 48 hours of your first payment, contact us and we&rsquo;ll make
        it right.</p>
        <p><strong className="text-white">Chargebacks.</strong> Attempting a
        chargeback without first contacting us is a material breach of these
        Terms and may result in immediate termination of services and
        collection efforts for any outstanding balance.</p>
      </Section>

      <Section title="5. Client Responsibilities">
        <ul className="list-disc pl-6 space-y-2">
          <li>Complete required health, injury, and medication questionnaires honestly and completely.</li>
          <li>Follow program instructions safely and within your physical capability. Stop and consult a physician if you experience pain or symptoms.</li>
          <li>Communicate progress, missed sessions, and issues promptly at your scheduled check-ins.</li>
          <li>Provide accurate contact and payment information.</li>
        </ul>
      </Section>

      <Section title="6. Peptides — Research Use Only">
        <p>Any peptide-related content or inquiry facility offered through
        NextGen Pro Peptides is provided <strong className="text-yellow-400">
        strictly for research use only</strong>. Peptides discussed are not
        for human or animal consumption and are not intended for medical or
        therapeutic use. You are solely responsible for compliance with all
        federal, state, and local laws governing the sale, purchase,
        possession, and use of peptide research compounds in your
        jurisdiction. See our full{" "}
        <a href="/medical-disclaimer" className="text-ng-blue hover:underline">Medical Disclaimer</a>.</p>
      </Section>

      <Section title="7. Intellectual Property">
        <p>All content on this site &mdash; including copy, custom workout plans, nutrition guides, meal templates, images, videos, logos, and the NEXTGEN PERFORMANCE and NEXTGEN PRO PEPTIDES brands &mdash; is the property of {BRAND.name} and is protected by copyright and trademark laws. You may not reproduce, redistribute, resell, or publicly share materials delivered to you as a client without our prior written consent.</p>
      </Section>

      <Section title="8. Assumption of Risk & Release">
        <p>Physical training involves inherent risks including muscle strain,
        joint injury, cardiovascular events, and, in rare cases, death.
        By using our services, you knowingly and voluntarily assume all such
        risks. To the fullest extent permitted by law, you release
        {BRAND.name}, Coach J, and any affiliated parties from all claims,
        demands, or damages arising from your participation, except in cases
        of gross negligence or willful misconduct.</p>
      </Section>

      <Section title="9. Limitation of Liability">
        <p>To the maximum extent permitted by law, our total aggregate
        liability for any claim arising out of or relating to these Terms
        or our services shall not exceed the amount you paid us in the
        <strong className="text-white"> 3 months</strong> preceding the event
        giving rise to the claim. In no event will we be liable for indirect,
        incidental, consequential, special, or punitive damages.</p>
      </Section>

      <Section title="10. Termination">
        <p>We may suspend or terminate your access to our services at any time, with or without cause or notice, including for violation of these Terms, non-payment, harassment of staff, or unlawful use of our services or peptide inquiry facility. Termination does not entitle you to any refund of prepaid fees.</p>
      </Section>

      <Section title="11. Governing Law & Disputes">
        <p>These Terms are governed by the laws of the State of Oregon, USA, without regard to conflict-of-law principles. Any dispute arising from these Terms or our services will first be attempted to be resolved by good-faith negotiation; if unresolved, disputes will be resolved by binding arbitration administered in Jackson County, Oregon, in accordance with the rules of the American Arbitration Association.</p>
      </Section>

      <Section title="12. Changes to These Terms">
        <p>We may update these Terms at any time. The updated version is effective when posted with a new &ldquo;Last Updated&rdquo; date. Continued use of the site or our services after a change constitutes acceptance of the updated Terms.</p>
      </Section>

      <Section title="13. Contact">
        <p>
          {BRAND.name}<br/>
          {BRAND.city} &middot; {BRAND.region}<br/>
          <a href={`mailto:${BRAND.email}`} className="text-ng-blue hover:underline">{BRAND.email}</a><br/>
          <a href={`tel:${BRAND.phone_link}`} className="text-ng-blue hover:underline">{BRAND.phone}</a>
        </p>
      </Section>

      <Note>
        These Terms are a general template and do not constitute legal advice.
        We recommend having a licensed attorney review these Terms for your
        specific business and jurisdiction before relying on them.
      </Note>
    </LegalLayout>
  );
}
